import NoResultsAfterFilters from 'assets/images/table/NoResultsAfterFilters.svg?react'
import { BreadcrumbHeader } from 'components/BreadcrumbSection/BreadcrumbHeader'
import { BackgroundMain } from 'components/Layout'
import { ErrorBox } from '@outbound-ai/ursa'
import { LoadingBox } from '@outbound-ai/ursa'
import { useCall } from 'features/calls/callsHooks'
import { PostCallChat, PostCallExtractedEntities, PostCallNotes } from 'features/calls/components'
import { ICall } from 'features/calls/types'
import { useClaim, useClaimCalls, useLevelofSupport } from 'features/claims/claimsHooks'
import { getCallIndexFromClaim } from 'features/claims/utils'
import { getClaimDetailPath, getClaimsPath } from 'features/claims/utils/claimsPathUtils'
import moment from 'moment'
import { createElement, useEffect, useRef, useState } from 'react'
import { Breadcrumb } from 'react-bootstrap'
import { renderToStaticMarkup } from 'react-dom/server'
import { FaCaretDown, FaCaretRight } from 'react-icons/fa'
import { useNavigate, useParams } from 'react-router-dom'
import { OBDropdown } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { ErrorMessagesEnum } from 'types/errorTypes'
import { formatPhoneNumber, sendLogs } from 'utils'
import { formatJobType } from 'utils/cellFormat'
import { errorHandler } from 'utils/errors/errors'
import { getTntIdForNavigation } from 'utils/tenantUrlUtils'
import { retrieveTenantId, isManualTenantSwitch } from 'utils/tenantUtils'
import { useAppSelector } from 'appState/store'
import { useUserProfile } from 'features/settings/settingsHooks'

export const ClaimCallsSummary = () => {
  // local state
  const hasTriedTenantRetrieval = useRef(false)
  const [isRetrievingTenant, setIsRetrievingTenant] = useState(false)

  // utils
  const {
    callId,
    oaiClaimId,
    tenantId: tenantIdFromParams,
  } = useParams<{ callId: string; oaiClaimId: string; tenantId: string }>()
  const navigate = useNavigate()

  // Get user profile for tenant lookup
  const { user } = useAppSelector((state) => state.auth)
  const { data: profile } = useUserProfile(user?.id)

  // Get tenant from URL params or fallback to helper (for old URL compatibility)
  const tenantId = tenantIdFromParams || getTntIdForNavigation()

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS (Rules of Hooks)
  const { data: claim, error: claimError, isLoading: isLoadingClaim } = useClaim(oaiClaimId || '')
  const { data: call, error: callError, isLoading: isLoadingCall } = useCall(callId || '')
  const { data: calls, isLoading: isLoadingCalls } = useClaimCalls(oaiClaimId || '')
  const levelOfSupportData = useLevelofSupport()

  // Early returns
  if (!callId || !oaiClaimId || !tenantId)
    return <ErrorBox title="Call id, claim id, or tenant id is missing from the parameters" />

  // If manual tenant switch is in progress, show loading state instead of trying to fetch claim
  // This prevents 404 errors during navigation
  if (isManualTenantSwitch()) {
    return <LoadingBox />
  }

  // If we're retrieving the correct tenant, show loading state instead of error
  if (isRetrievingTenant) {
    return <LoadingBox />
  }

  // Handle claim errors - try to retrieve correct tenant first before redirecting
  useEffect(() => {
    if (claimError && oaiClaimId && !hasTriedTenantRetrieval.current && profile) {
      sendLogs({ message: `ClaimsCallSummary.tsx ~ claim error: ${claimError}` })

      // Mark that we've tried tenant retrieval to prevent infinite loops
      hasTriedTenantRetrieval.current = true

      // Skip automatic tenant switching if this is a manual tenant switch
      // Manual switches handle navigation in TenantSelection component
      if (isManualTenantSwitch()) {
        return
      }

      // Show loading state while retrieving tenant
      setIsRetrievingTenant(true)

      // Try to retrieve the correct tenant for this claim (automatic switching)
      // If successful, retrieveTenantId will reload the page with correct tenant
      retrieveTenantId({
        claimId: oaiClaimId,
        profile,
        onError: () => {
          // If retrieveTenantId fails (different customer or no access),
          // the error message is already set in sessionStorage by retrieveTenantId
          // Use window.location.href to ensure the page reloads and shows the notification
          window.location.href = getClaimsPath(tenantId)
        },
      })
    }
  }, [claimError, oaiClaimId, tenantId, navigate, profile])

  // Save level of support to localStorage
  useEffect(() => {
    if (levelOfSupportData.data) {
      localStorage.setItem('levelOfSupport', JSON.stringify(levelOfSupportData.data))
    }
  }, [levelOfSupportData.data])

  // CONDITIONAL RETURNS AFTER ALL HOOKS
  // Early returns
  if (!callId || !oaiClaimId || !tenantId)
    return <ErrorBox title="Call id, claim id, or tenant id is missing from the parameters" />

  // If manual tenant switch is in progress, show loading state instead of trying to fetch claim
  // This prevents 404 errors during navigation
  if (isManualTenantSwitch()) {
    return <LoadingBox />
  }

  // If we're retrieving the correct tenant, show loading state instead of error
  if (isRetrievingTenant) {
    return <LoadingBox />
  }

  const redirectToClaimDetails = () => {
    navigate(getClaimDetailPath(tenantId, oaiClaimId!))
  }

  const redirectToClaims = () => {
    navigate(getClaimsPath(tenantId))
  }

  useEffect(() => {
    if (!claim || !oaiClaimId || !call || !callId) return

    if (!claim.claimId || claim.claimId === oaiClaimId) return

    const parts = window.location.pathname.split('/')
    const claimIndex = parts.lastIndexOf('claim')
    const callIndex = parts.lastIndexOf('calls-summary')
    if (claimIndex === -1 || callIndex === -1) return

    if (parts[claimIndex + 1] !== oaiClaimId || parts[callIndex + 1] !== callId) return

    const newClaimId = claim.claimId
    const newCallId = (call as any).jbId ?? (call as any).jobId ?? callId

    parts[claimIndex + 1] = newClaimId
    parts[callIndex + 1] = String(newCallId)
    const newPath = parts.join('/')

    navigate(newPath, { replace: true })
  }, [claim, oaiClaimId, call, callId, navigate])

  // variables
  const hasCallResources = call && claim && !isLoadingCalls && !callError && !claimError
  const isLoadingPage = isLoadingClaim || isLoadingCall || isLoadingCalls
  const error = callError || claimError
  const breadCrumbTitle = calls ? `Call #${getCallIndexFromClaim(calls, callId!)}` : ''
  const getInitiatingUserName = (call: ICall) =>
    call?.initiatorUser == null
      ? 'AI Agent'
      : ((call.initiatorUser.firstName ?? '') + ' ' + (call.initiatorUser.lastName ?? '')).trim()

  /*
  const callDispositions = keys(CallDispositionEnum) as (keyof typeof CallDispositionEnum)[]
  const disposition = call?.disposition || 0
  const isOwner = isCurrentUserClaimOwner(claim?.ownerId, user!) */
  /* const togglerText =
    typeof call?.disposition === 'number'
      ? CallDispositionEnum[callDispositions[disposition]]
      : isOwner
      ? 'Select Outcome'
      : 'Pending disposition from owner' */

  return (
    <>
      <BackgroundMain />
      <Div>
        <BreadcrumbSectionContainerDiv>
          <BreadcrumbHeader
            title={breadCrumbTitle}
            /* titleComponent={
              hasCallResources && call.useCase === UseCaseEnumToUseCaseIdEnum.CSI ? (
                <>
                  <Button variant="flat" radius="rounded" onClick={() => setShow(true)} disabled={claim.archived}>
                    {togglerText} {isOwner ? <FaEdit /> : ''}
                  </Button>
                  <SelectCallDispositionModal call={call} claim={claim} show={show} setShow={setShow} />
                </>
              ) : (
                <></>
              )
            } */
            backAction={() => redirectToClaimDetails()}
            navComponent={
              <Breadcrumb className="call0summary-breadcrumb">
                <Breadcrumb.Item onClick={() => redirectToClaims()}>Claims</Breadcrumb.Item>
                <Breadcrumb.Item onClick={() => redirectToClaimDetails()}>Claim Details</Breadcrumb.Item>
                <Breadcrumb.Item active>Call Summary</Breadcrumb.Item>
              </Breadcrumb>
            }
            breadcrumbs={[
              { title: 'Claims', path: getClaimsPath(tenantId) },
              { title: 'Claim Details', path: getClaimDetailPath(tenantId, oaiClaimId) },
              { title: 'Post-Call Summary' },
            ]}
            showBackButton={true}
            showVerticalSeparator={false}
            suppressTooltip={true}
          >
            <>
              <section className="call-disposition-container">
                {/* add menu of calls */}

                <OBDropdown
                  variant="default"
                  radius="rounded"
                  width="200px"
                  hideCaret={true}
                  buttonName={
                    <>
                      <div>{calls ? `Call #${getCallIndexFromClaim(calls, callId!)}` : 'Call #1'}</div>
                      <div>
                        <FaCaretDown className="ms-1" />
                      </div>
                    </>
                  }
                  title={calls ? `Call #${getCallIndexFromClaim(calls, callId!)}` : 'Call #1'}
                  items={[
                    {
                      items:
                        calls?.map((call, callIndex) => ({
                          text: (
                            <CallList>
                              <div className="callTitle">Call #{getCallIndexFromClaim(calls, call.jbId)}</div>
                              <div className="callInitiator">
                                {getInitiatingUserName(call)} call to
                                <br /> {formatPhoneNumber(calls[callIndex].phoneNumber)}
                              </div>
                              <div className="callTime">
                                {moment(calls[callIndex].jobStarted).local().format('MM/DD/YYYY')} at{' '}
                                {moment(calls[callIndex].jobStarted).local().format('hh:mm A')}{' '}
                              </div>
                              {formatJobType(calls[callIndex].jobType, calls[callIndex].useCase!)}
                            </CallList>
                          ),
                          onClick: () => {
                            const location = window.location.pathname.split('/')
                            location[location.length - 1] = call.jbId
                            const newUrl = location.join('/')
                            window.location.replace(newUrl)
                          },
                        })) || [],
                    },
                  ]}
                />
              </section>
            </>
          </BreadcrumbHeader>
        </BreadcrumbSectionContainerDiv>
        <main>
          {error && (
            <ErrorBox
              image={<NoResultsAfterFilters />}
              {...errorHandler.errorToErrorBoxProps(error, ErrorMessagesEnum.CallError)}
            />
          )}
          {isLoadingPage && <LoadingBox />}

          {hasCallResources && (
            <>
              <div className="call-info">
                <PostCallExtractedEntities call={call} claim={claim} />
                <PostCallNotes callId={call.jobId} claim={claim} />
              </div>
              <div className="call-chat">
                <PostCallChat call={call} claim={claim} />
              </div>
            </>
          )}
        </main>
      </Div>
    </>
  )
}

const BreadcrumbSectionContainerDiv = styled.div`
  padding-right: ${({ theme }) => theme.spacing.l};
  padding-bottom: ${({ theme }) => theme.spacing.l}!important;
  height: 84px !important;

  .left-section {
    button {
      margin-top: 1.8rem !important;
    }
  }
`

const CallList = styled.div`
  .callInitiator {
    ${({ theme }) => theme.typography.s.medium};
    color: ${({ theme }) => theme.colors.neutral.darker};
    margin-bottom: ${({ theme }) => theme.spacing.xxxs};
    line-height: 1.5;
  }
  .callTitle {
    ${({ theme }) => theme.typography.s.medium};
    color: ${({ theme }) => theme.colors.neutral.darker};
    margin-bottom: ${({ theme }) => theme.spacing.xxxs};
  }
  .callTime {
    ${({ theme }) => theme.typography.xs.normal};
    color: ${({ theme }) => theme.colors.grey.neutral};
    margin-bottom: ${({ theme }) => theme.spacing.xxxs};
  }
`

const reactSvgComponentToMarkupString = (Component: any, props: any) =>
  `data:image/svg+xml,${encodeURIComponent(renderToStaticMarkup(createElement(Component, props)))}`

const Div = styled.div`
  height: calc(99vh - ${({ theme }) => theme.constants.navbarwithBreadcrumbsHeight});
  position: relative;
  overflow: hidden;

  .left-section-breadcrumbs {
    margin-top: 0.5rem;
  }

  .breadcrumb {
    width: 15.9375rem;
    position: relative;
    top: 5px;
    background-color: none;
    li {
      a {
        ${({ theme }) => theme.typography.xs.medium};
        color: ${({ theme }) => theme.colors.info.neutral};
        cursor: pointer;
        text-decoration: none !important;
      }
    }
    .breadcrumb-item.active {
      ${({ theme }) => theme.typography.xs.medium};
      color: ${({ theme }) => theme.colors.grey.dark};
    }

    .breadcrumb-item + .breadcrumb-item::before {
      content: ${({ theme }) =>
        `url(${reactSvgComponentToMarkupString(FaCaretRight, {
          color: theme.colors.grey.neutral,
        })})`}!important;
      margin-top: 2px;
    }
  }

  .ob-dropdown {
    button {
      #dropdown-basic {
        display: flex !important;
        justify-content: space-between !important;
      }
      display: flex !important;
      justify-content: space-between !important;
    }
  }

  #dropdown-basic {
    display: flex !important;
    justify-content: space-between !important;
  }
  .dropdown-menu {
    min-width: 250px;
    max-width: 300px !important;

    .callInitiator {
      ${({ theme }) => theme.typography.s.normal};
      color: ${({ theme }) => theme.colors.neutral.darker};
    }
  }

  > main {
    height: 100%;
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, ${({ theme }) => theme.util.pixelToRem(466)});
    grid-template-rows: 1fr, minmax(min-content, ${({ theme }) => theme.util.pixelToRem(300)});
    grid-template-areas:
      'call-info call-chat'
      'call-notes call-notes';
    padding-bottom: 2rem;
    height: calc(100% - ${({ theme }) => theme.constants.breadcrumbHeight});
    overflow: auto;

    .call-chat {
      grid-area: call-chat;
      margin-bottom: 0.625rem;
      border-bottom: 1px solid ${({ theme }) => theme.colors.grey.lighter};
      overflow: hidden;
      height: 100%;
      min-width: 0;
      max-width: ${({ theme }) => theme.util.pixelToRem(466)};
      width: 100%;
    }

    .call-notes {
      grid-area: call-notes;
      padding: 0.5rem 1.5rem 1rem;
      height: 100%;
    }

    .call-info {
      position: relative;
      grid-area: call-info;
      padding: 1rem 1.5rem 0.5rem;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-width: 0;
    }
  }
`
